import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DirectService, PropertyPhotoTag } from '@app/modules/direct/services/direct.service';
import { PropertyTag } from '@app/shared/interfaces';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PropertyDetailResyncDialogComponent } from '../property-detail-resync-dialog/property-detail-resync-dialog.component';

interface CheckoutTask {
  task_detail: string;
  is_present: boolean;
}

interface CheckoutTasks {
  [key: string]: CheckoutTask;
}

interface CheckoutTaskItem {
  type: string;
  task_detail: string;
}

@Component({
  standalone: false,
  selector: 'sbnb-property-detail-details',
  templateUrl: './property-detail-details.component.html',
  styleUrls: ['./property-detail-details.component.scss'],
})
export class PropertyDetailDetailsComponent implements OnInit {
  @Input('property') readonly origProperty: Readonly<any>;
  property: any;
  saveStatus = 0;

  @ViewChild('newTagElm') newTagElm: any;
  addTagBool: boolean;

  mapOptions: google.maps.MapOptions = {};

  editSummary = false;
  resyncing: boolean;

  roomsEdit = false;
  photosEdit = false;

  check_in_hours: number;
  check_in_minutes: number;
  check_out_hours: number;
  check_out_minutes: number;

  availableAmenities = [];

  propertyPhotoTags: Observable<PropertyPhotoTag[]>;

  accountPropertyTags: PropertyTag[];
  newTag = new UntypedFormControl('');
  filteredAccountPropertyTags: Observable<PropertyTag[]>;

  checkoutTasksEdit = false;
  showNewTaskForm = false;
  newTaskType: string | null = null;
  newTaskDetail = '';

  readonly CHECKOUT_TASK_TYPES = [
    { value: 'RETURN_KEYS', label: 'Return keys' },
    { value: 'TURN_THINGS_OFF', label: 'Turn things off' },
    { value: 'THROW_TRASH', label: 'Take care of trash' },
    { value: 'LOCK_UP', label: 'Lock up' },
    { value: 'GATHER_TOWELS', label: 'Gather towels' },
    { value: 'ADDITIONAL_REQUESTS', label: 'Additional requests' }
  ];

  public editedCheckoutTasks: CheckoutTaskItem[] = [];
  public displayedCheckoutTasks: CheckoutTaskItem[] = [];

  hasCheckoutTasksFeature = false;

  public isSaveDisabled = false;

  constructor(
    private readonly propertyService: PropertiesService,
    private readonly dialog: MatDialog,
    private directService: DirectService,
    private optimizelyService: OptimizelyService
  ) {
    this.optimizelyService.reviewCategoryTagsEnabled$.subscribe(
      enabled => this.hasCheckoutTasksFeature = enabled
    );
  }

  ngOnInit(): void {
    this.property = Object.assign({ pictures: [] }, { ...this.origProperty, tags: this.origProperty.tags.sort() });

    this.fetchAccountPropertyTags();

    this.propertyService.availableAmenities().subscribe((res) => {
      this.availableAmenities = res;
    });

    this.fetchPropertyImageTags(this.property.id);

    this.parseCheckInOutTimes();
    this.updateDisplayedTasks();  // Initial load of tasks
  }

  fetchAccountPropertyTags() {
    this.propertyService.getAccountPropertyTags().subscribe((res) => {
      this.accountPropertyTags = res;

      if (this.newTag) {
        this.filteredAccountPropertyTags = this.newTag.valueChanges.pipe(
          startWith(''),
          map((value: any) => {
            const name = typeof value === 'string' ? value : value?.name;
            return name ? this._filter(name as string) : this.accountPropertyTagsNotUsed;
          })
        );
      }
    });
  }

  get accountPropertyTagsNotUsed(): PropertyTag[] {
    return this.accountPropertyTags
      ? this.accountPropertyTags.filter((option) => {
        if (!option || !option.tag) {
          return false;
        }

        return this.property.tags &&
          this.property.tags.map((tag) => tag.toLowerCase()).includes(option.tag.toLowerCase())
          ? false
          : true;
      })
      : [];
  }

  displayFn(propertyTag: PropertyTag): string {
    return propertyTag && propertyTag.tag ? propertyTag.tag : '';
  }

  private _filter(name: string): PropertyTag[] {
    const filterValue = name.toLowerCase();
    return this.accountPropertyTagsNotUsed.filter((option) =>
      option && option.tag ? option.tag.toLowerCase().includes(filterValue) : false
    );
  }

  fetchPropertyImageTags(propertyId: string) {
    this.propertyPhotoTags = this.directService.getPhotoTagsForProperty(propertyId);
  }

  parseCheckInOutTimes() {
    if (this.property.check_in_time) {
      this.check_in_hours = this.property.check_in_time.split(':')[0];
      this.check_in_minutes = this.property.check_in_time.split(':')[1];
    } else {
      this.check_in_hours = 14;
      this.check_in_minutes = 0;
    }

    if (this.property.check_out_time) {
      this.check_out_hours = this.property.check_out_time.split(':')[0];
      this.check_out_minutes = this.property.check_out_time.split(':')[1];
    } else {
      this.check_out_hours = 11;
      this.check_out_minutes = 0;
    }
  }

  checkInOutTimesChanged(event, type) {
    if (event.minutes >= 0 && event.minutes < 10) {
      event.minutes = `0${event.minutes}`;
    }

    this.property[`${type}time`] = `${event.hours}:${event.minutes}`;
    this.updateProperty();
  }

  isTagAlreadyOnAccount(tag): boolean {
    return this.accountPropertyTags.some((accountTag) =>
      accountTag && accountTag.tag ? accountTag.tag.toLowerCase() === tag.toLowerCase() : false
    );
  }

  addTag(newTagValue) {
    if (this.property.tags.findIndex((tag) => tag === newTagValue) === -1) {
      this.property.tags.push(newTagValue);
      !this.isTagAlreadyOnAccount(newTagValue)
        ? this.accountPropertyTags.push({
          tag: newTagValue,
          propertyId: this.property.id,
        })
        : null;
      this.updateProperty();
    }
    this.newTagElm.value = '';
    this.newTag.reset();
    this.addTagBool = false;
  }

  removeTag(tagValue) {
    const index = this.property.tags.findIndex((tag) => tag === tagValue);

    if (index !== -1) {
      this.property.tags.splice(index, 1);
      this.updateProperty();
      this.addTagBool = false;
    }
  }

  toggleAddTag() {
    this.addTagBool = !this.addTagBool;
    if (this.addTagBool) {
      setTimeout(() => {
        this.newTagElm.nativeElement.focus();
      }, 100);
    }
  }

  updateProperty() {
    this.saveStatus = 1;
    this.updateDisplayedTasks();

    this.propertyService.updateProperty(this.property.id, this.property).subscribe((res) => {
      if (res.error) {
        this.saveStatus = -1;
      } else {
        this.saveStatus = 2;
        this.property = res.data;
      }
      this.updateDisplayedTasks();
    });
  }

  resync() {
    const dialogRef = this.dialog.open(PropertyDetailResyncDialogComponent, {
      width: '720px',
      data: {
        property: this.property,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res.data) {
        this.property = res.data;
      }
    });
  }

  get checkoutTasksList(): CheckoutTaskItem[] {
    if (!this.property.checkout_tasks) return [];

    return Object.entries(this.property.checkout_tasks)
      .filter(([_, task]: [string, CheckoutTask]) => task.is_present)
      .map(([type, task]: [string, CheckoutTask]) => ({
        type,
        task_detail: task.task_detail || ''
      }));
  }

  get availableCheckoutTasks() {
    const usedTasks = new Set(this.editedCheckoutTasks.map(task => task.type));
    return this.CHECKOUT_TASK_TYPES.filter(task => !usedTasks.has(task.value));
  }

  getTaskDisplayName(taskType: string): string {
    const task = this.CHECKOUT_TASK_TYPES.find(t => t.value === taskType);
    return task?.label || taskType;
  }

  getTaskMaxLength(taskType: string): number {
    return taskType === 'ADDITIONAL_REQUESTS' ? 400 : 140;
  }

  checkoutTasksEditMode() {
    this.checkoutTasksEdit = true;
    this.editedCheckoutTasks = this.checkoutTasksList.map(task => ({
      type: task.type,
      task_detail: task.task_detail || ''
    }));
  }

  cancelCheckoutTasksEdit() {
    this.checkoutTasksEdit = false;
    this.showNewTaskForm = false;
    this.editedCheckoutTasks = [];
  }

  private canSaveCheckoutTasks(): boolean {
    const additionalRequestTask = this.editedCheckoutTasks.find(task =>
      task.type === 'ADDITIONAL_REQUESTS'
    );

    if (additionalRequestTask && (!additionalRequestTask.task_detail || !additionalRequestTask.task_detail.trim())) {
      return false;
    }

    return true;
  }

  saveCheckoutTasks() {
    if (!this.canSaveCheckoutTasks()) {
      return;
    }

    // Reset checkout_tasks to empty object
    this.property.checkout_tasks = {};

    // Add only the active tasks
    this.editedCheckoutTasks.forEach(task => {
      this.property.checkout_tasks[task.type] = {
        task_detail: task.task_detail,
        is_present: true
      };
    });

    this.checkoutTasksEdit = false;
    this.showNewTaskForm = false;
    this.editedCheckoutTasks = [];
    this.updateProperty();
  }

  removeCheckoutTask(taskType: string) {
    this.editedCheckoutTasks = this.editedCheckoutTasks.filter(task => task.type !== taskType);
    if (this.property.checkout_tasks?.[taskType]) {
      this.property.checkout_tasks[taskType].is_present = false;
    }
  }

  addNewCheckoutTask() {
    if (!this.newTaskType) return;

    this.editedCheckoutTasks.push({
      type: this.newTaskType,
      task_detail: this.newTaskDetail || ''
    });

    this.cancelNewTask();
  }

  cancelNewTask() {
    this.showNewTaskForm = false;
    this.newTaskType = null;
    this.newTaskDetail = '';
  }

  startNewTask(task: { value: string, label: string }) {
    this.showNewTaskForm = false;
    this.editedCheckoutTasks = [
      ...this.editedCheckoutTasks,
      {
        type: task.value,
        task_detail: ''
      }
    ];
    this.isSaveDisabled = !this.canSaveCheckoutTasks();
  }

  toggleNewTaskMenu() {
    this.showNewTaskForm = !this.showNewTaskForm;
  }

  onTaskDetailChange() {
    this.isSaveDisabled = !this.canSaveCheckoutTasks();
  }

  private updateDisplayedTasks() {
    if (!this.property.checkout_tasks) {
      this.displayedCheckoutTasks = [];
      return;
    }

    this.displayedCheckoutTasks = Object.entries(this.property.checkout_tasks)
      .filter(([_, task]: [string, CheckoutTask]) => task.is_present)
      .map(([type, task]: [string, CheckoutTask]) => ({
        type,
        task_detail: task.task_detail || ''
      }));
  }
}
